/**
 * SIGNING OUT, from every screen with a Sign out: Account, at its foot since slice A1 moved it
 * there from Home's, the holding screen and the page that says to call. Each screen draws its own Sign
 * out where its design puts it, and this is what pressing it does, and the line beside it.
 *
 * - ONCE THE WORKER HAS SAID SIGNED OUT, the screen asks /api/me again (`onSignedOut`, from
 *   `Entry`), whose answer is the door, which takes the place of the screen and the focus to its
 *   heading (`screens/Door.tsx`). So it does when she has moved to another place before the answer
 *   came: every place is the same `Entry`, which stays drawn, so the door takes the place she is on.
 * - ONE THAT DOES NOT GO THROUGH, an error or a request that never reached the Worker, leaves her
 *   where she is, signed in, and says so as every failed ask does: Sign out stays drawn and is never
 *   disabled, so the focus stays on it, and the live region beside it, drawn with the screen, says
 *   it did not go through, with the phone. Pressed again, the line goes until the answer comes.
 *   Once she has moved to another place, the screen and its line have gone, and nothing is said.
 * - ONE AT A TIME. While one is out, another press does nothing.
 * - WHAT HER BROWSER HELD OF HER PAYING, every payment on its way and the invoice this tab left to
 *   pay (`lib/paying.ts`), is forgotten once the Worker has said signed out, and only then: one that
 *   does not go through leaves her signed in, and her Pay still held.
 */
import { useEffect, useRef, useState } from 'react';
import { signOut } from '../api.ts';
import { forgetAll } from '../lib/paying.ts';
import { FAILED_BEFORE_PHONE } from '../words.ts';
import { Phone } from './Phone.tsx';

/** What a screen's Sign out needs. */
export interface SigningOut {
  /** Signs out, for Sign out to call when it is pressed. */
  readonly signOut: () => void;
  /** Whether the last sign-out did not go through. */
  readonly failed: boolean;
}

/** Signing out, and then `onSignedOut` once the Worker has said so. */
export function useSignOut(onSignedOut: () => void): SigningOut {
  const [failed, setFailed] = useState(false);
  /** A sign-out is out, so another press does nothing. */
  const out = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function go() {
    if (out.current) return;
    out.current = true;
    setFailed(false);
    const answer = await signOut();
    out.current = false;
    // Signed out is signed out wherever she is by now: `onSignedOut` is Entry's, which stays drawn
    // when she moves between places, so it is called even once this screen has gone. The line that
    // says it did not go through is this screen's alone, and is not drawn once it has gone.
    if (answer === 'signed_out') {
      // Nothing of her paying is held for whoever signs in next on this browser.
      forgetAll();
      onSignedOut();
    } else if (mounted.current) setFailed(true);
  }

  return { signOut: () => void go(), failed };
}

/**
 * The live region beside a Sign out, drawn with the screen so a screen reader knows it before it
 * speaks, and the line it says once a sign-out did not go through.
 */
export function SignOutFailed({ failed }: { readonly failed: boolean }) {
  return (
    <div aria-live="polite">
      {failed ? (
        <p className="mt-3 text-body text-pretty text-text-body">
          {FAILED_BEFORE_PHONE} <Phone after="." />
        </p>
      ) : null}
    </div>
  );
}
